/**
 * Career Pathways Demo Data
 *
 * Client-specific mock data for demos. Switch between clients to show
 * industry-relevant roles, programs, and metrics.
 *
 * Also includes scenario system for demonstrating different user states:
 * - full: Power user with all benefits and history
 * - first-login: New employee seeing the platform for first time
 * - ta-only: Company only offers Tuition Assistance
 * - slrp-only: Company only offers Student Loan Repayment
 * - new-employee: 30 days in, some setup done
 * - maxed-out: Used all benefits this year
 */

import type CompanySettingModel from 'tio-data/models/company-setting';
import type PlanModel from 'tio-data/models/plan';

// =============================================================================
// DEMO SCENARIOS - Different user/config states to demonstrate
// =============================================================================

const hasOwn = <T extends object>(object: T, key: PropertyKey): key is keyof T =>
  Object.prototype.hasOwnProperty.call(object, key);

export type DemoScenarioId =
  | 'full'
  | 'first-login'
  | 'ta-only'
  | 'slrp-only'
  | 'no-ta'
  | 'new-employee'
  | 'maxed-out'
  | 'wellness'
  | 'pslf-wellness'
  | 'student-loan-contributions'
  | 'secure-2-match'
  | 'flexible-choice-plan';

export type DemoTasScenarioId =
  | 'not-started'
  | 'in-progress'
  | 'approved'
  | 'rejected'
  | 'completed';

export type DemoPslfScenarioId =
  | 'blank-slate'
  | 'borrower-signed'
  | 'employer-signed'
  | 'submitted'
  | 'form-rejected'
  | 'multi-form'
  | 'with-nslds'
  | 'full-journey'
  | 'live-data';

export type DemoProductId =
  | 'wellness'
  | 'pslf-wellness'
  | 'student-loan-contributions'
  | 'secure-2-retirement-match'
  | 'tuition-assistance'
  | 'flexible-choice-plan';

export type DemoSessionRoleId = 'employee' | 'supervisor' | 'admin';

export type DemoUserScenarioId =
  | 'first-login'
  | 'new-employee'
  | 'power-user'
  | 'benefits-maxed'
  | 'flex-choice-hsa';

export type DemoTaPaymentModelId = 'reimbursement' | 'prepayment' | 'reimbursement-with-deferral';

export interface DemoScenario {
  id: DemoScenarioId;
  name: string;
  description: string;
  features: {
    tuitionAssistance: boolean;
    studentLoanRepayment: boolean;
    careerPathways: boolean;
    financialWellness: boolean;
    pslf: boolean;
    employerMatch: boolean;
  };
  userState: {
    isFirstLogin: boolean;
    daysEmployed: number;
    hasLinkedLoans: boolean;
    hasActiveTAApplication: boolean;
    benefitsMaxedOut: boolean;
    setupComplete: boolean;
    careerPlanCreated: boolean;
    skillsAssessed: boolean;
    taScenario?: DemoTasScenarioId;
  };
  messaging: {
    welcomeTitle: string;
    welcomeSubtitle: string;
    ctaText: string;
    ctaRoute?: string;
  };
}

export interface DemoProduct {
  id: DemoProductId;
  name: string;
  description: string;
  scenarioId: DemoScenarioId;
  tasScenarioId?: DemoTasScenarioId;
  pslfScenarioId?: DemoPslfScenarioId;
}

export interface DemoSessionRole {
  id: DemoSessionRoleId;
  name: string;
  description: string;
  relationshipType: string;
}

export interface DemoUserScenario {
  id: DemoUserScenarioId;
  name: string;
  description: string;
  scenarioId: DemoScenarioId;
}

export interface DemoTaPaymentModel {
  id: DemoTaPaymentModelId;
  name: string;
  description: string;
}

export interface DemoSessionSelection {
  clientId: DemoClientId;
  productId: DemoProductId;
  roleId: DemoSessionRoleId;
  userScenarioId?: DemoUserScenarioId;
  taPaymentModelId?: DemoTaPaymentModelId;
}

export type DemoCompanySettings = Partial<CompanySettingModel>;
export type DemoPlanSummary = Pick<PlanModel, 'cadence' | 'isWellness'> & {
  id: string;
  name: string;
  annualLimit: number;
  lifetimeLimit: number;
  tiers: { amount: number }[];
};

export interface DemoPreRegisterCompany {
  id: string;
  name: string;
  launchDate: string;
  programTitle: string;
  taEnabled: boolean;
  settings: DemoCompanySettings;
}

export interface DemoPartnerCompany extends DemoPreRegisterCompany {
  companySetting: DemoCompanySettings;
  plans: DemoPlanSummary[];
}

export const DEMO_PRODUCTS: Record<DemoProductId, DemoProduct> = {
  wellness: {
    id: 'wellness',
    name: 'Wellness',
    description: 'Financial wellness journey without loan or tuition modules',
    scenarioId: 'wellness',
  },
  'pslf-wellness': {
    id: 'pslf-wellness',
    name: 'PSLF Wellness',
    description: 'Loan wellness plus PSLF guidance and form workflow',
    scenarioId: 'pslf-wellness',
    pslfScenarioId: 'full-journey',
  },
  'student-loan-contributions': {
    id: 'student-loan-contributions',
    name: 'Student Loan Contributions',
    description: 'Employer student loan repayment contribution experience',
    scenarioId: 'student-loan-contributions',
  },
  'secure-2-retirement-match': {
    id: 'secure-2-retirement-match',
    name: 'Secure 2.0 Retirement Match',
    description: 'Qualified student loan payment match demo for retirement readiness',
    scenarioId: 'secure-2-match',
  },
  'tuition-assistance': {
    id: 'tuition-assistance',
    name: 'Tuition Assistance',
    description: 'Tuition assistance application and education pathway experience',
    scenarioId: 'ta-only',
    tasScenarioId: 'in-progress',
  },
  'flexible-choice-plan': {
    id: 'flexible-choice-plan',
    name: 'Flexible Choice Plan',
    description: 'Forward-looking multi-benefit choice experience',
    scenarioId: 'flexible-choice-plan',
    tasScenarioId: 'approved',
    pslfScenarioId: 'full-journey',
  },
};

export const DEMO_PRODUCT_LIST = Object.values(DEMO_PRODUCTS);

export const DEMO_SESSION_ROLES: Record<DemoSessionRoleId, DemoSessionRole> = {
  employee: {
    id: 'employee',
    name: 'Employee',
    description: 'Participant benefit experience',
    relationshipType: 'PARTICIPANT',
  },
  supervisor: {
    id: 'supervisor',
    name: 'Supervisor',
    description: 'Supervisor approval and team support experience',
    relationshipType: 'TAS.Approver.SUPERVISOR',
  },
  admin: {
    id: 'admin',
    name: 'Admin',
    description: 'Employer admin reporting and management experience',
    relationshipType: 'EMPLOYEE_ADMIN',
  },
};

export const DEMO_SESSION_ROLE_LIST = Object.values(DEMO_SESSION_ROLES);

export const DEMO_USER_SCENARIOS: Record<DemoUserScenarioId, DemoUserScenario> = {
  'first-login': {
    id: 'first-login',
    name: 'First Login',
    description: 'Brand new account, nothing configured yet',
    scenarioId: 'first-login',
  },
  'new-employee': {
    id: 'new-employee',
    name: 'New Employee',
    description: '30 days in, partial setup completed',
    scenarioId: 'new-employee',
  },
  'power-user': {
    id: 'power-user',
    name: 'Power User',
    description: 'Fully engaged employee with active benefits and history',
    scenarioId: 'full',
  },
  'benefits-maxed': {
    id: 'benefits-maxed',
    name: 'Benefits Maxed',
    description: 'Used all benefits available this year',
    scenarioId: 'maxed-out',
  },
  'flex-choice-hsa': {
    id: 'flex-choice-hsa',
    name: 'Flex Choice · HDHP / HSA',
    description: 'Early-career employee on an HDHP funding their HSA through Flex Choice',
    scenarioId: 'flexible-choice-plan',
  },
};

export const DEMO_USER_SCENARIO_LIST = Object.values(DEMO_USER_SCENARIOS);

export const DEMO_TA_PAYMENT_MODELS: Record<DemoTaPaymentModelId, DemoTaPaymentModel> = {
  reimbursement: {
    id: 'reimbursement',
    name: 'Reimbursement',
    description: 'Employee pays out of pocket and is reimbursed after course completion',
  },
  prepayment: {
    id: 'prepayment',
    name: 'Prepayment (CourseCash)',
    description: 'Employer prepays the school directly via CourseCash before the course starts',
  },
  'reimbursement-with-deferral': {
    id: 'reimbursement-with-deferral',
    name: 'Reimbursement w/ Deferral',
    description:
      'School defers tuition until after course start; employee is reimbursed within the deferral window',
  },
};

export const DEMO_TA_PAYMENT_MODEL_LIST = Object.values(DEMO_TA_PAYMENT_MODELS);

// Every persisted demo-session key under the `reimagined-demo-*` namespace.
// `clearDemoSession()` removes this whole set. NOTE: the picker
// (`applyDemoSession`) additionally overloads the plain, non-namespaced `role`
// key with the demo relationship type, recording that it did so via
// `reimagined-demo-role-overloaded`. That `role` key is shared with the real
// user's role selection, so `clearDemoSession()` drops it ONLY when that overload
// marker is present — NOT for the in-page toggle or a pure `?demo=true` view,
// which never touch `role`.
const DEMO_MODE_STORAGE_KEYS = [
  'reimagined-demo-mode',
  'reimagined-demo-client',
  'reimagined-demo-product',
  'reimagined-demo-role',
  'reimagined-demo-role-overloaded',
  'reimagined-demo-scenario',
  'reimagined-demo-user-scenario',
  'reimagined-demo-ta-payment-model',
  'reimagined-demo-ta-scenario',
  'reimagined-demo-pslf-scenario',
];

function isReimaginedRoutePath(pathname: string): boolean {
  return pathname.split('/').includes('reimagined');
}

// The admin / partner-admin experience lives at the TOP-LEVEL /admin/* (the
// `authenticated` route segment is pathless, so it never appears in the URL). A
// sticky demo session must stay active across these routes too, so an admin-role
// demo (EMPLOYEE_ADMIN / TAS.Approver.SUPERVISOR) can click through the admin app
// without re-arming demo (?demo=true) on every link. Match only the FIRST path
// segment — not any nested segment that happens to equal "admin" (e.g. a record
// id) — to keep the sticky-demo boundary tight.
function isAdminRoutePath(pathname: string): boolean {
  return pathname.split('/').filter(Boolean)[0] === 'admin';
}

// Routes where a sticky (explicitly opted-in) demo session renders demo data:
// the reimagined employee experience and the classic admin experience.
function isDemoableRoutePath(pathname: string): boolean {
  return isReimaginedRoutePath(pathname) || isAdminRoutePath(pathname);
}

// Only an explicit `?demo=true` arms demo mode from the URL. The other `demo_*`
// params are session REFINEMENTS (client / product / role / scenario) that are
// consumed only once demo is already active — their bare presence in an
// incidental query string (a marketing or analytics link, a stale share) must
// NEVER activate demo for a real user. The canonical demo deep-link
// (`buildDemoSessionUrl`) always sets `demo=true`, so legitimate links and the
// picker are unaffected.
function hasDemoModeUrlParam(params: URLSearchParams): boolean {
  return params.get('demo') === 'true';
}

function hasStickyDemoMode(pathname: string): boolean {
  return isDemoableRoutePath(pathname) && localStorage.getItem('reimagined-demo-mode') === 'true';
}

/**
 * Whether the current request should render demo data. This is a PURE READ —
 * it must never write persistent state. `?demo=true` activates demo for the
 * current navigation; the sticky flag keeps it active across the demoable
 * routes (the reimagined employee experience AND the /admin experience) ONLY
 * after a user has explicitly opted in via the picker (`applyDemoSession`) or
 * the reimagined demo toggle, and is cleared by `clearDemoSession()` on
 * logout / toggle-off.
 *
 * This function previously wrote the sticky flag as a side effect of merely
 * reading activation state, so a single stray demo link permanently armed demo
 * mode for a non-demo user — the root of the live regression where demo
 * fixtures (e.g. `demo-slr-company-1`) 404'd the app for real users. A
 * predicate named `is…()` must not mutate persisted state.
 */
export function isReimaginedDemoMode(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  if (hasDemoModeUrlParam(params)) {
    return true;
  }

  return hasStickyDemoMode(window.location.pathname);
}

/**
 * Clear ALL persisted demo-session state. Called on logout (so a demo session
 * never survives session invalidation, nor leaks to the next user on a shared
 * device) and by the manual toggle-off. A no-op outside the browser.
 */
export function clearDemoSession(): void {
  if (typeof window === 'undefined') {
    return;
  }
  // The plain, non-namespaced `role` key is shared with the real user's role
  // selection. ONLY the picker (`applyDemoSession`) overloads it with a demo
  // relationship type, and it stamps `reimagined-demo-role-overloaded` when it
  // does. Drop `role` only when that marker is present — captured BEFORE the loop
  // clears it. The in-page toggle and a pure `?demo=true` view never overload
  // `role`, so clearing it for them would wipe the real user's own selection
  // (and on a shared device it would survive past the demo). The sticky flag
  // alone is NOT a reliable proxy: the toggle sets it without overloading `role`.
  const roleWasOverloadedByDemo =
    localStorage.getItem('reimagined-demo-role-overloaded') === 'true';
  for (const key of DEMO_MODE_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }
  if (roleWasOverloadedByDemo) {
    localStorage.removeItem('role');
  }
}

const DEMO_SCENARIOS: Record<DemoScenarioId, DemoScenario> = {
  full: {
    id: 'full',
    name: 'Power User',
    description: 'Fully engaged employee with all features',
    features: {
      tuitionAssistance: true,
      studentLoanRepayment: true,
      careerPathways: true,
      financialWellness: true,
      pslf: true,
      employerMatch: true,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 730,
      hasLinkedLoans: true,
      hasActiveTAApplication: true,
      benefitsMaxedOut: false,
      setupComplete: true,
      careerPlanCreated: true,
      skillsAssessed: true,
      taScenario: 'in-progress',
    },
    messaging: {
      welcomeTitle: 'Welcome back, Maria!',
      welcomeSubtitle: "You're making great progress on your goals",
      ctaText: 'View Dashboard',
    },
  },
  'first-login': {
    id: 'first-login',
    name: 'First Login',
    description: 'New employee seeing platform for first time',
    features: {
      tuitionAssistance: true,
      studentLoanRepayment: true,
      careerPathways: true,
      financialWellness: true,
      pslf: true,
      employerMatch: true,
    },
    userState: {
      isFirstLogin: true,
      daysEmployed: 0,
      hasLinkedLoans: false,
      hasActiveTAApplication: false,
      benefitsMaxedOut: false,
      setupComplete: false,
      careerPlanCreated: false,
      skillsAssessed: false,
      taScenario: 'not-started',
    },
    messaging: {
      welcomeTitle: 'Welcome to Your Benefits! 🎉',
      welcomeSubtitle: "Let's get you set up to maximize your education benefits",
      ctaText: 'Get Started',
      ctaRoute: 'authenticated.reimagined.index',
    },
  },
  'ta-only': {
    id: 'ta-only',
    name: 'TA Only',
    description: 'Company only offers Tuition Assistance',
    features: {
      tuitionAssistance: true,
      studentLoanRepayment: false,
      careerPathways: true,
      financialWellness: false,
      pslf: false,
      employerMatch: false,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 365,
      hasLinkedLoans: false,
      hasActiveTAApplication: true,
      benefitsMaxedOut: false,
      setupComplete: true,
      careerPlanCreated: true,
      skillsAssessed: true,
      taScenario: 'in-progress',
    },
    messaging: {
      welcomeTitle: 'Ready to Learn?',
      welcomeSubtitle: 'Your employer offers up to $5,250 in tuition assistance',
      ctaText: 'Explore Programs',
    },
  },
  'slrp-only': {
    id: 'slrp-only',
    name: 'SLRP Only',
    description: 'Company only offers Student Loan Repayment',
    features: {
      tuitionAssistance: false,
      studentLoanRepayment: true,
      careerPathways: false,
      financialWellness: true,
      pslf: true,
      employerMatch: true,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 180,
      hasLinkedLoans: true,
      hasActiveTAApplication: false,
      benefitsMaxedOut: false,
      setupComplete: true,
      careerPlanCreated: false,
      skillsAssessed: false,
      taScenario: 'not-started',
    },
    messaging: {
      welcomeTitle: 'Crushing Your Debt!',
      welcomeSubtitle: 'Your employer contributes $125/month toward your loans',
      ctaText: 'View Progress',
    },
  },
  'no-ta': {
    id: 'no-ta',
    name: 'No TA',
    description: 'Tuition Assistance not available to this employee',
    features: {
      tuitionAssistance: false,
      studentLoanRepayment: true,
      careerPathways: true,
      financialWellness: true,
      pslf: true,
      employerMatch: true,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 365,
      hasLinkedLoans: true,
      hasActiveTAApplication: false,
      benefitsMaxedOut: false,
      setupComplete: true,
      careerPlanCreated: true,
      skillsAssessed: true,
      taScenario: 'not-started',
    },
    messaging: {
      welcomeTitle: 'Welcome Back!',
      welcomeSubtitle:
        'Your employer offers student loan repayment and career development benefits',
      ctaText: 'View Benefits',
    },
  },
  'new-employee': {
    id: 'new-employee',
    name: 'New Employee',
    description: '30 days in, some setup completed',
    features: {
      tuitionAssistance: true,
      studentLoanRepayment: true,
      careerPathways: true,
      financialWellness: true,
      pslf: true,
      employerMatch: true,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 30,
      hasLinkedLoans: true,
      hasActiveTAApplication: false,
      benefitsMaxedOut: false,
      setupComplete: false,
      careerPlanCreated: false,
      skillsAssessed: false,
      taScenario: 'not-started',
    },
    messaging: {
      welcomeTitle: "You're Off to a Great Start!",
      welcomeSubtitle: 'Complete your profile to unlock personalized recommendations',
      ctaText: 'Continue Setup',
    },
  },
  'maxed-out': {
    id: 'maxed-out',
    name: 'Benefits Maxed',
    description: 'Used all benefits this year',
    features: {
      tuitionAssistance: true,
      studentLoanRepayment: true,
      careerPathways: true,
      financialWellness: true,
      pslf: true,
      employerMatch: true,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 1095,
      hasLinkedLoans: true,
      hasActiveTAApplication: false,
      benefitsMaxedOut: true,
      setupComplete: true,
      careerPlanCreated: true,
      skillsAssessed: true,
      taScenario: 'completed',
    },
    messaging: {
      welcomeTitle: 'Amazing Year! 🏆',
      welcomeSubtitle: "You've maximized your 2026 benefits. Plan for next year!",
      ctaText: 'Plan for 2027',
    },
  },
  wellness: {
    id: 'wellness',
    name: 'Wellness',
    description: 'Financial wellness benefit focus',
    features: {
      tuitionAssistance: false,
      studentLoanRepayment: false,
      careerPathways: false,
      financialWellness: true,
      pslf: false,
      employerMatch: false,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 180,
      hasLinkedLoans: false,
      hasActiveTAApplication: false,
      benefitsMaxedOut: false,
      setupComplete: true,
      careerPlanCreated: false,
      skillsAssessed: false,
      taScenario: 'not-started',
    },
    messaging: {
      welcomeTitle: 'Build Financial Confidence',
      welcomeSubtitle: 'Explore personalized wellness steps for your goals',
      ctaText: 'View Wellness Plan',
    },
  },
  'pslf-wellness': {
    id: 'pslf-wellness',
    name: 'PSLF Wellness',
    description: 'Loan wellness with PSLF support',
    features: {
      tuitionAssistance: false,
      studentLoanRepayment: true,
      careerPathways: false,
      financialWellness: true,
      pslf: true,
      employerMatch: false,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 365,
      hasLinkedLoans: true,
      hasActiveTAApplication: false,
      benefitsMaxedOut: false,
      setupComplete: true,
      careerPlanCreated: false,
      skillsAssessed: false,
      taScenario: 'not-started',
    },
    messaging: {
      welcomeTitle: 'Stay on Track for Forgiveness',
      welcomeSubtitle: 'Review your PSLF progress and next best action',
      ctaText: 'Review PSLF Progress',
    },
  },
  'student-loan-contributions': {
    id: 'student-loan-contributions',
    name: 'Student Loan Contributions',
    description: 'Employer contribution benefit focus',
    features: {
      tuitionAssistance: false,
      studentLoanRepayment: true,
      careerPathways: false,
      financialWellness: true,
      pslf: false,
      employerMatch: false,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 270,
      hasLinkedLoans: true,
      hasActiveTAApplication: false,
      benefitsMaxedOut: false,
      setupComplete: true,
      careerPlanCreated: false,
      skillsAssessed: false,
      taScenario: 'not-started',
    },
    messaging: {
      welcomeTitle: 'Your Employer Is Helping Pay Down Debt',
      welcomeSubtitle: 'See contributions, loan progress, and payoff impact',
      ctaText: 'View Contributions',
    },
  },
  'secure-2-match': {
    id: 'secure-2-match',
    name: 'Secure 2.0 Retirement Match',
    description: 'Student loan payment match toward retirement',
    features: {
      tuitionAssistance: false,
      studentLoanRepayment: true,
      careerPathways: false,
      financialWellness: true,
      pslf: false,
      employerMatch: true,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 540,
      hasLinkedLoans: true,
      hasActiveTAApplication: false,
      benefitsMaxedOut: false,
      setupComplete: true,
      careerPlanCreated: false,
      skillsAssessed: false,
      taScenario: 'not-started',
    },
    messaging: {
      welcomeTitle: 'Turn Loan Payments into Retirement Progress',
      welcomeSubtitle: 'Track qualifying payments and projected match value',
      ctaText: 'View Match Strategy',
    },
  },
  'flexible-choice-plan': {
    id: 'flexible-choice-plan',
    name: 'Flexible Choice Plan',
    description: 'Multi-benefit plan with flexible education choices',
    features: {
      tuitionAssistance: true,
      studentLoanRepayment: true,
      careerPathways: true,
      financialWellness: true,
      pslf: true,
      employerMatch: true,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 730,
      hasLinkedLoans: true,
      hasActiveTAApplication: true,
      benefitsMaxedOut: false,
      setupComplete: true,
      careerPlanCreated: true,
      skillsAssessed: true,
      taScenario: 'approved',
    },
    messaging: {
      welcomeTitle: 'Choose the Benefit Path That Fits Today',
      welcomeSubtitle: 'Compare education, loan, and retirement-match opportunities',
      ctaText: 'Explore Choices',
    },
  },
};

export const DEMO_SCENARIO_LIST = [
  DEMO_SCENARIOS.full,
  DEMO_SCENARIOS['first-login'],
  DEMO_SCENARIOS['ta-only'],
  DEMO_SCENARIOS['slrp-only'],
  DEMO_SCENARIOS['no-ta'],
  DEMO_SCENARIOS['new-employee'],
  DEMO_SCENARIOS['maxed-out'],
];

// =============================================================================
// CLIENT TYPES
// =============================================================================

export type DemoClientId =
  | 'lifepoint'
  | 'advocate'
  | 'starbucks'
  | 'delaware'
  | 'university-of-miami'
  | 'shelby-county'
  | 'verizon'
  | 'ups'
  | 'daffy'
  | 'legal';

export interface DemoClient {
  id: DemoClientId;
  name: string;
  industry: string;
  logo?: string;
  employeeCount: number;
  taUtilization: number;
  planActivation: number;
  completionRate: number;
  internalMobility: number;
}

export interface DemoRole {
  id: string;
  title: string;
  department: string;
  level: string;
  salary: string;
  openings: number;
  growth: string;
  skills: string[];
}

export interface DemoProgram {
  name: string;
  provider: string;
  type: string;
  duration: string;
  cost: string;
  completionRate: number;
  roi: number;
  enrollments: number;
}

export interface DemoPipelineStage {
  name: string;
  count: number;
  description: string;
}

export interface DemoCriticalRole {
  title: string;
  gap: number;
  risk: 'critical' | 'high' | 'medium' | 'low';
  retirements: number;
  pipeline: number;
}

export interface DemoCompetency {
  name: string;
  avgScore: number;
  target: number;
  gap: number;
}

export interface DemoClientData {
  client: DemoClient;
  criticalRoles: DemoCriticalRole[];
  topPrograms: DemoProgram[];
  pipeline: {
    identified: number;
    inProgram: number;
    developing: number;
    readyNow: number;
    promoted: number;
    attrition: number;
  };
  competencies: DemoCompetency[];
  departments: Array<{ name: string; employees: number; pipeline: number }>;
  employee?: {
    firstName: string;
    lastName: string;
  };
}

// =============================================================================
// LIFEPOINT HEALTH - Community Healthcare
// =============================================================================
const LIFEPOINT_HEALTH: DemoClientData = {
  client: {
    id: 'lifepoint',
    name: 'Lifepoint Health',
    industry: 'Healthcare',
    employeeCount: 50000,
    taUtilization: 18,
    planActivation: 27,
    completionRate: 79,
    internalMobility: 14,
  },
  criticalRoles: [
    { title: 'Registered Nurse (RN)', gap: 520, risk: 'critical', retirements: 135, pipeline: 245 },
    { title: 'Clinical Supervisor', gap: 72, risk: 'high', retirements: 20, pipeline: 38 },
    { title: 'Medical Assistant', gap: 180, risk: 'high', retirements: 48, pipeline: 110 },
    { title: 'Imaging Technologist', gap: 64, risk: 'medium', retirements: 18, pipeline: 34 },
    { title: 'Revenue Cycle Specialist', gap: 45, risk: 'medium', retirements: 12, pipeline: 28 },
  ],
  topPrograms: [
    {
      name: 'RN-to-BSN Completion',
      provider: 'Western Governors University',
      type: 'Degree',
      duration: '18 months',
      cost: '$10,500',
      completionRate: 84,
      roi: 238,
      enrollments: 420,
    },
    {
      name: 'Clinical Leadership Essentials',
      provider: 'Internal',
      type: 'Development',
      duration: '10 weeks',
      cost: '$2,200',
      completionRate: 90,
      roi: 275,
      enrollments: 310,
    },
    {
      name: 'Medical Assistant Certification',
      provider: 'NHA',
      type: 'Certificate',
      duration: '5 months',
      cost: '$1,800',
      completionRate: 88,
      roi: 210,
      enrollments: 260,
    },
    {
      name: 'Radiologic Technology Bridge',
      provider: 'Community College Network',
      type: 'Certificate',
      duration: '12 months',
      cost: '$6,500',
      completionRate: 76,
      roi: 195,
      enrollments: 95,
    },
    {
      name: 'Healthcare Revenue Cycle',
      provider: 'HFMA',
      type: 'Certificate',
      duration: '8 weeks',
      cost: '$1,100',
      completionRate: 92,
      roi: 180,
      enrollments: 140,
    },
  ],
  pipeline: {
    identified: 1650,
    inProgram: 980,
    developing: 360,
    readyNow: 120,
    promoted: 88,
    attrition: 150,
  },
  competencies: [
    { name: 'Patient Care', avgScore: 86, target: 94, gap: 8 },
    { name: 'Clinical Judgment', avgScore: 78, target: 88, gap: 10 },
    { name: 'Team Leadership', avgScore: 70, target: 84, gap: 14 },
    { name: 'Care Coordination', avgScore: 74, target: 86, gap: 12 },
    { name: 'Technology Adoption', avgScore: 68, target: 82, gap: 14 },
  ],
  departments: [
    { name: 'Nursing', employees: 21000, pipeline: 620 },
    { name: 'Clinical Operations', employees: 9500, pipeline: 230 },
    { name: 'Ambulatory Care', employees: 7200, pipeline: 180 },
    { name: 'Diagnostics', employees: 5100, pipeline: 85 },
    { name: 'Administrative Services', employees: 7200, pipeline: 65 },
  ],
};

// =============================================================================
// ADVOCATE HEALTH - Healthcare System
// =============================================================================
const ADVOCATE_HEALTH: DemoClientData = {
  client: {
    id: 'advocate',
    name: 'Advocate Health',
    industry: 'Healthcare',
    employeeCount: 155000,
    taUtilization: 15,
    planActivation: 23,
    completionRate: 78,
    internalMobility: 12,
  },
  criticalRoles: [
    {
      title: 'Registered Nurse (RN)',
      gap: 1250,
      risk: 'critical',
      retirements: 340,
      pipeline: 520,
    },
    { title: 'Nurse Manager', gap: 85, risk: 'high', retirements: 28, pipeline: 45 },
    { title: 'Clinical Pharmacist', gap: 42, risk: 'high', retirements: 15, pipeline: 18 },
    { title: 'Medical Technologist', gap: 78, risk: 'medium', retirements: 22, pipeline: 35 },
    { title: 'Respiratory Therapist', gap: 34, risk: 'medium', retirements: 12, pipeline: 20 },
  ],
  topPrograms: [
    {
      name: 'RN-to-BSN Completion',
      provider: 'Chamberlain University',
      type: 'Degree',
      duration: '18 months',
      cost: '$12,000',
      completionRate: 82,
      roi: 245,
      enrollments: 1240,
    },
    {
      name: 'Nurse Leadership Certificate',
      provider: 'AONL',
      type: 'Certificate',
      duration: '6 months',
      cost: '$3,500',
      completionRate: 91,
      roi: 312,
      enrollments: 380,
    },
    {
      name: 'Clinical Ladder Advancement',
      provider: 'Internal',
      type: 'Development',
      duration: '12 months',
      cost: '$2,000',
      completionRate: 88,
      roi: 278,
      enrollments: 520,
    },
    {
      name: 'MSN - Nurse Practitioner',
      provider: 'Loyola University',
      type: 'Graduate',
      duration: '24 months',
      cost: '$45,000',
      completionRate: 76,
      roi: 185,
      enrollments: 145,
    },
    {
      name: 'Pharmacy Technician Cert',
      provider: 'ExCPT',
      type: 'Certificate',
      duration: '4 months',
      cost: '$1,200',
      completionRate: 94,
      roi: 220,
      enrollments: 210,
    },
  ],
  pipeline: {
    identified: 2800,
    inProgram: 1850,
    developing: 620,
    readyNow: 185,
    promoted: 142,
    attrition: 280,
  },
  competencies: [
    { name: 'Clinical Excellence', avgScore: 82, target: 90, gap: 8 },
    { name: 'Patient Safety', avgScore: 88, target: 95, gap: 7 },
    { name: 'Team Leadership', avgScore: 72, target: 85, gap: 13 },
    { name: 'Change Management', avgScore: 65, target: 80, gap: 15 },
    { name: 'Financial Acumen', avgScore: 58, target: 75, gap: 17 },
    { name: 'Technology Adoption', avgScore: 70, target: 85, gap: 15 },
  ],
  departments: [
    { name: 'Nursing', employees: 68000, pipeline: 1200 },
    { name: 'Clinical Services', employees: 25000, pipeline: 380 },
    { name: 'Pharmacy', employees: 8500, pipeline: 120 },
    { name: 'Imaging & Diagnostics', employees: 12000, pipeline: 95 },
    { name: 'Administration', employees: 18000, pipeline: 55 },
  ],
};

// =============================================================================
// UPS - Logistics & Delivery
// =============================================================================
const UPS_DATA: DemoClientData = {
  client: {
    id: 'ups',
    name: 'UPS',
    industry: 'Logistics & Delivery',
    employeeCount: 534000,
    taUtilization: 8,
    planActivation: 18,
    completionRate: 71,
    internalMobility: 22,
  },
  criticalRoles: [
    { title: 'Package Car Driver', gap: 4200, risk: 'critical', retirements: 1800, pipeline: 2100 },
    { title: 'Hub Supervisor', gap: 380, risk: 'high', retirements: 95, pipeline: 220 },
    {
      title: 'Feeder Driver (CDL-A)',
      gap: 1100,
      risk: 'critical',
      retirements: 420,
      pipeline: 380,
    },
    { title: 'Operations Manager', gap: 125, risk: 'medium', retirements: 45, pipeline: 85 },
    { title: 'Industrial Engineer', gap: 65, risk: 'medium', retirements: 18, pipeline: 42 },
  ],
  topPrograms: [
    {
      name: 'CDL-A Training Program',
      provider: 'UPS Integrad',
      type: 'License',
      duration: '6 weeks',
      cost: '$8,000',
      completionRate: 88,
      roi: 420,
      enrollments: 3200,
    },
    {
      name: 'Supervisor Leadership',
      provider: 'Internal',
      type: 'Development',
      duration: '12 weeks',
      cost: '$2,500',
      completionRate: 92,
      roi: 385,
      enrollments: 1800,
    },
    {
      name: 'Supply Chain Management',
      provider: 'ASU Online',
      type: 'Degree',
      duration: '24 months',
      cost: '$18,000',
      completionRate: 68,
      roi: 195,
      enrollments: 420,
    },
    {
      name: 'Six Sigma Green Belt',
      provider: 'ASQ',
      type: 'Certificate',
      duration: '3 months',
      cost: '$2,800',
      completionRate: 85,
      roi: 245,
      enrollments: 680,
    },
    {
      name: 'Industrial Engineering Tech',
      provider: 'Purdue Global',
      type: 'Degree',
      duration: '36 months',
      cost: '$28,000',
      completionRate: 62,
      roi: 168,
      enrollments: 185,
    },
  ],
  pipeline: {
    identified: 8500,
    inProgram: 5200,
    developing: 1850,
    readyNow: 620,
    promoted: 485,
    attrition: 720,
  },
  competencies: [
    { name: 'Safety & Compliance', avgScore: 92, target: 98, gap: 6 },
    { name: 'Operations Excellence', avgScore: 85, target: 90, gap: 5 },
    { name: 'Team Leadership', avgScore: 72, target: 85, gap: 13 },
    { name: 'Technology Systems', avgScore: 68, target: 82, gap: 14 },
    { name: 'Customer Service', avgScore: 78, target: 88, gap: 10 },
    { name: 'Problem Solving', avgScore: 75, target: 85, gap: 10 },
  ],
  departments: [
    { name: 'Package Operations', employees: 285000, pipeline: 4800 },
    { name: 'Feeders & Transportation', employees: 125000, pipeline: 2100 },
    { name: 'Hub Operations', employees: 85000, pipeline: 1200 },
    { name: 'Engineering', employees: 12000, pipeline: 280 },
    { name: 'Corporate & Admin', employees: 27000, pipeline: 120 },
  ],
};

// =============================================================================
// STARBUCKS - Retail & Food Service
// =============================================================================
const STARBUCKS_DATA: DemoClientData = {
  client: {
    id: 'starbucks',
    name: 'Starbucks',
    industry: 'Retail & Food Service',
    employeeCount: 402000,
    taUtilization: 42,
    planActivation: 38,
    completionRate: 74,
    internalMobility: 35,
  },
  criticalRoles: [
    { title: 'Store Manager', gap: 850, risk: 'critical', retirements: 120, pipeline: 680 },
    { title: 'Shift Supervisor', gap: 2100, risk: 'high', retirements: 180, pipeline: 1850 },
    { title: 'District Manager', gap: 95, risk: 'high', retirements: 35, pipeline: 72 },
    { title: 'Regional Director', gap: 18, risk: 'medium', retirements: 8, pipeline: 22 },
    { title: 'Licensed Store Manager', gap: 220, risk: 'medium', retirements: 45, pipeline: 165 },
  ],
  topPrograms: [
    {
      name: 'Starbucks College Achievement (ASU)',
      provider: 'Arizona State University',
      type: 'Degree',
      duration: '48 months',
      cost: '$0 (100% covered)',
      completionRate: 68,
      roi: 520,
      enrollments: 28000,
    },
    {
      name: 'Store Manager Development',
      provider: 'Internal',
      type: 'Development',
      duration: '16 weeks',
      cost: '$3,200',
      completionRate: 88,
      roi: 385,
      enrollments: 4200,
    },
    {
      name: 'Shift Supervisor Readiness',
      provider: 'Internal',
      type: 'Development',
      duration: '8 weeks',
      cost: '$1,500',
      completionRate: 92,
      roi: 290,
      enrollments: 8500,
    },
    {
      name: 'District Manager Academy',
      provider: 'Internal',
      type: 'Leadership',
      duration: '12 weeks',
      cost: '$8,000',
      completionRate: 95,
      roi: 445,
      enrollments: 320,
    },
    {
      name: 'Food Safety Certification',
      provider: 'ServSafe',
      type: 'Certificate',
      duration: '1 day',
      cost: '$180',
      completionRate: 98,
      roi: 150,
      enrollments: 52000,
    },
  ],
  pipeline: {
    identified: 42000,
    inProgram: 28500,
    developing: 8200,
    readyNow: 2800,
    promoted: 2150,
    attrition: 4800,
  },
  competencies: [
    { name: 'Customer Connection', avgScore: 88, target: 95, gap: 7 },
    { name: 'Beverage Quality', avgScore: 92, target: 95, gap: 3 },
    { name: 'Team Development', avgScore: 78, target: 88, gap: 10 },
    { name: 'Store Operations', avgScore: 82, target: 90, gap: 8 },
    { name: 'Financial Management', avgScore: 65, target: 80, gap: 15 },
    { name: 'Community Building', avgScore: 85, target: 90, gap: 5 },
  ],
  departments: [
    { name: 'Retail Stores', employees: 340000, pipeline: 38000 },
    { name: 'Licensed Stores', employees: 35000, pipeline: 2200 },
    { name: 'Roasting & Manufacturing', employees: 8000, pipeline: 180 },
    { name: 'Supply Chain', employees: 12000, pipeline: 280 },
    { name: 'Corporate Support', employees: 7000, pipeline: 140 },
  ],
};

// =============================================================================
// STATE OF DELAWARE - Public Sector
// =============================================================================
const STATE_OF_DELAWARE: DemoClientData = {
  client: {
    id: 'delaware',
    name: 'State of Delaware',
    industry: 'Public Sector',
    employeeCount: 30000,
    taUtilization: 21,
    planActivation: 32,
    completionRate: 81,
    internalMobility: 19,
  },
  criticalRoles: [
    {
      title: 'Social Services Specialist',
      gap: 185,
      risk: 'critical',
      retirements: 48,
      pipeline: 96,
    },
    { title: 'Corrections Officer', gap: 260, risk: 'critical', retirements: 72, pipeline: 125 },
    { title: 'IT Security Analyst', gap: 42, risk: 'high', retirements: 8, pipeline: 24 },
    { title: 'Public Health Nurse', gap: 58, risk: 'high', retirements: 18, pipeline: 31 },
    { title: 'Program Manager', gap: 36, risk: 'medium', retirements: 16, pipeline: 22 },
  ],
  topPrograms: [
    {
      name: 'Public Administration Certificate',
      provider: 'University of Delaware',
      type: 'Certificate',
      duration: '6 months',
      cost: '$3,600',
      completionRate: 87,
      roi: 205,
      enrollments: 230,
    },
    {
      name: 'Cybersecurity Fundamentals',
      provider: 'Wilmington University',
      type: 'Certificate',
      duration: '16 weeks',
      cost: '$2,400',
      completionRate: 83,
      roi: 260,
      enrollments: 150,
    },
    {
      name: 'Human Services Leadership',
      provider: 'Internal',
      type: 'Development',
      duration: '10 weeks',
      cost: '$1,500',
      completionRate: 91,
      roi: 215,
      enrollments: 310,
    },
    {
      name: 'Criminal Justice Administration',
      provider: 'Delaware Technical Community College',
      type: 'Degree',
      duration: '24 months',
      cost: '$9,800',
      completionRate: 75,
      roi: 185,
      enrollments: 170,
    },
    {
      name: 'Public Health Practice',
      provider: 'Johns Hopkins Bloomberg School',
      type: 'Certificate',
      duration: '12 weeks',
      cost: '$2,200',
      completionRate: 86,
      roi: 195,
      enrollments: 90,
    },
  ],
  pipeline: {
    identified: 1250,
    inProgram: 760,
    developing: 280,
    readyNow: 105,
    promoted: 74,
    attrition: 118,
  },
  competencies: [
    { name: 'Constituent Service', avgScore: 84, target: 92, gap: 8 },
    { name: 'Policy Execution', avgScore: 76, target: 88, gap: 12 },
    { name: 'Digital Government', avgScore: 68, target: 84, gap: 16 },
    { name: 'Program Management', avgScore: 72, target: 86, gap: 14 },
    { name: 'Compliance', avgScore: 82, target: 92, gap: 10 },
  ],
  departments: [
    { name: 'Health & Social Services', employees: 9200, pipeline: 330 },
    { name: 'Correction', employees: 4800, pipeline: 240 },
    { name: 'Education', employees: 4100, pipeline: 115 },
    { name: 'Technology & Information', employees: 1400, pipeline: 75 },
    { name: 'Administration', employees: 10500, pipeline: 160 },
  ],
};

// =============================================================================
// UNIVERSITY OF MIAMI - Higher Education & Healthcare
// =============================================================================
const UNIVERSITY_OF_MIAMI: DemoClientData = {
  client: {
    id: 'university-of-miami',
    name: 'University of Miami',
    industry: 'Higher Education & Healthcare',
    employeeCount: 19000,
    taUtilization: 34,
    planActivation: 41,
    completionRate: 83,
    internalMobility: 24,
  },
  criticalRoles: [
    {
      title: 'Clinical Research Coordinator',
      gap: 74,
      risk: 'high',
      retirements: 14,
      pipeline: 42,
    },
    { title: 'Registered Nurse (RN)', gap: 220, risk: 'critical', retirements: 56, pipeline: 118 },
    { title: 'Student Success Advisor', gap: 38, risk: 'medium', retirements: 10, pipeline: 26 },
    { title: 'Research Administrator', gap: 45, risk: 'high', retirements: 12, pipeline: 22 },
    { title: 'IT Systems Analyst', gap: 32, risk: 'medium', retirements: 7, pipeline: 20 },
  ],
  topPrograms: [
    {
      name: 'Healthcare MBA',
      provider: 'University of Miami Patti and Allan Herbert Business School',
      type: 'Graduate',
      duration: '24 months',
      cost: '$48,000',
      completionRate: 79,
      roi: 190,
      enrollments: 86,
    },
    {
      name: 'Clinical Research Operations',
      provider: 'Internal',
      type: 'Certificate',
      duration: '12 weeks',
      cost: '$2,800',
      completionRate: 88,
      roi: 235,
      enrollments: 140,
    },
    {
      name: 'Higher Ed Leadership',
      provider: 'ACE',
      type: 'Development',
      duration: '8 weeks',
      cost: '$2,200',
      completionRate: 91,
      roi: 215,
      enrollments: 120,
    },
    {
      name: 'RN-to-BSN Completion',
      provider: 'University of Miami School of Nursing',
      type: 'Degree',
      duration: '18 months',
      cost: '$14,500',
      completionRate: 82,
      roi: 230,
      enrollments: 210,
    },
    {
      name: 'Research Administration Certificate',
      provider: 'SRA International',
      type: 'Certificate',
      duration: '10 weeks',
      cost: '$1,900',
      completionRate: 86,
      roi: 205,
      enrollments: 95,
    },
  ],
  pipeline: {
    identified: 920,
    inProgram: 610,
    developing: 210,
    readyNow: 92,
    promoted: 63,
    attrition: 84,
  },
  competencies: [
    { name: 'Research Operations', avgScore: 78, target: 90, gap: 12 },
    { name: 'Student Support', avgScore: 84, target: 92, gap: 8 },
    { name: 'Clinical Quality', avgScore: 82, target: 92, gap: 10 },
    { name: 'Data Literacy', avgScore: 70, target: 86, gap: 16 },
    { name: 'Inclusive Leadership', avgScore: 80, target: 90, gap: 10 },
  ],
  departments: [
    { name: 'UHealth', employees: 8200, pipeline: 310 },
    { name: 'Academic Affairs', employees: 3900, pipeline: 135 },
    { name: 'Research', employees: 2400, pipeline: 110 },
    { name: 'Student Life', employees: 1700, pipeline: 70 },
    { name: 'Administration', employees: 2800, pipeline: 85 },
  ],
};

// =============================================================================
// SHELBY COUNTY PUBLIC SCHOOLS - K-12 Education
// =============================================================================
const SHELBY_COUNTY_PUBLIC_SCHOOLS: DemoClientData = {
  client: {
    id: 'shelby-county',
    name: 'Shelby County Public Schools',
    industry: 'K-12 Education',
    employeeCount: 14000,
    taUtilization: 29,
    planActivation: 36,
    completionRate: 77,
    internalMobility: 18,
  },
  criticalRoles: [
    {
      title: 'Special Education Teacher',
      gap: 160,
      risk: 'critical',
      retirements: 42,
      pipeline: 68,
    },
    { title: 'STEM Teacher', gap: 135, risk: 'critical', retirements: 35, pipeline: 74 },
    { title: 'School Counselor', gap: 48, risk: 'high', retirements: 16, pipeline: 26 },
    { title: 'Assistant Principal', gap: 28, risk: 'medium', retirements: 12, pipeline: 21 },
    { title: 'Bilingual Educator', gap: 74, risk: 'high', retirements: 18, pipeline: 34 },
  ],
  topPrograms: [
    {
      name: 'Teacher Licensure Pathway',
      provider: 'University of Memphis',
      type: 'Degree',
      duration: '24 months',
      cost: '$12,000',
      completionRate: 74,
      roi: 215,
      enrollments: 320,
    },
    {
      name: 'Special Education Endorsement',
      provider: 'Tennessee State University',
      type: 'Certificate',
      duration: '9 months',
      cost: '$4,800',
      completionRate: 82,
      roi: 240,
      enrollments: 180,
    },
    {
      name: 'Instructional Leadership',
      provider: 'Internal',
      type: 'Development',
      duration: '12 weeks',
      cost: '$1,600',
      completionRate: 89,
      roi: 225,
      enrollments: 210,
    },
    {
      name: 'School Counseling Bridge',
      provider: 'Vanderbilt Peabody Online',
      type: 'Graduate',
      duration: '24 months',
      cost: '$32,000',
      completionRate: 76,
      roi: 175,
      enrollments: 60,
    },
    {
      name: 'Bilingual Education Certificate',
      provider: 'Regional Education Cooperative',
      type: 'Certificate',
      duration: '6 months',
      cost: '$2,900',
      completionRate: 84,
      roi: 210,
      enrollments: 115,
    },
  ],
  pipeline: {
    identified: 780,
    inProgram: 480,
    developing: 185,
    readyNow: 62,
    promoted: 44,
    attrition: 92,
  },
  competencies: [
    { name: 'Instructional Practice', avgScore: 80, target: 90, gap: 10 },
    { name: 'Student Support', avgScore: 82, target: 92, gap: 10 },
    { name: 'Classroom Technology', avgScore: 70, target: 86, gap: 16 },
    { name: 'Inclusive Education', avgScore: 76, target: 90, gap: 14 },
    { name: 'Leadership Readiness', avgScore: 68, target: 84, gap: 16 },
  ],
  departments: [
    { name: 'Elementary Schools', employees: 5200, pipeline: 210 },
    { name: 'Middle Schools', employees: 2600, pipeline: 115 },
    { name: 'High Schools', employees: 3300, pipeline: 125 },
    { name: 'Student Support', employees: 1500, pipeline: 70 },
    { name: 'District Operations', employees: 1400, pipeline: 45 },
  ],
};

// =============================================================================
// VERIZON - Telecommunications
// =============================================================================
const VERIZON_DATA: DemoClientData = {
  client: {
    id: 'verizon',
    name: 'Verizon',
    industry: 'Telecommunications',
    employeeCount: 105000,
    taUtilization: 24,
    planActivation: 44,
    completionRate: 80,
    internalMobility: 21,
  },
  criticalRoles: [
    { title: 'Network Engineer', gap: 420, risk: 'critical', retirements: 96, pipeline: 220 },
    { title: 'Cybersecurity Analyst', gap: 210, risk: 'high', retirements: 35, pipeline: 115 },
    { title: 'Retail Store Manager', gap: 310, risk: 'high', retirements: 78, pipeline: 185 },
    { title: 'Cloud Solutions Architect', gap: 145, risk: 'high', retirements: 24, pipeline: 72 },
    { title: 'Customer Experience Lead', gap: 190, risk: 'medium', retirements: 55, pipeline: 120 },
  ],
  topPrograms: [
    {
      name: '5G Network Engineering',
      provider: 'Internal',
      type: 'Certificate',
      duration: '14 weeks',
      cost: '$3,800',
      completionRate: 86,
      roi: 310,
      enrollments: 950,
    },
    {
      name: 'Cybersecurity Professional',
      provider: 'SANS Institute',
      type: 'Certificate',
      duration: '16 weeks',
      cost: '$8,500',
      completionRate: 82,
      roi: 245,
      enrollments: 430,
    },
    {
      name: 'Cloud Architecture',
      provider: 'AWS Training',
      type: 'Certificate',
      duration: '12 weeks',
      cost: '$2,400',
      completionRate: 84,
      roi: 285,
      enrollments: 520,
    },
    {
      name: 'Retail Leadership Academy',
      provider: 'Internal',
      type: 'Development',
      duration: '10 weeks',
      cost: '$1,900',
      completionRate: 91,
      roi: 260,
      enrollments: 1200,
    },
    {
      name: 'Data Analytics for Telecom',
      provider: 'Coursera / Google',
      type: 'Certificate',
      duration: '6 months',
      cost: '$1,400',
      completionRate: 78,
      roi: 220,
      enrollments: 360,
    },
  ],
  pipeline: {
    identified: 4200,
    inProgram: 2550,
    developing: 930,
    readyNow: 310,
    promoted: 240,
    attrition: 380,
  },
  competencies: [
    { name: 'Network Architecture', avgScore: 78, target: 90, gap: 12 },
    { name: 'Security Operations', avgScore: 74, target: 88, gap: 14 },
    { name: 'Customer Experience', avgScore: 84, target: 92, gap: 8 },
    { name: 'Cloud Platforms', avgScore: 72, target: 86, gap: 14 },
    { name: 'Data Fluency', avgScore: 70, target: 84, gap: 14 },
  ],
  departments: [
    { name: 'Network & Technology', employees: 31000, pipeline: 1350 },
    { name: 'Consumer Retail', employees: 28000, pipeline: 960 },
    { name: 'Business Markets', employees: 18000, pipeline: 640 },
    { name: 'Security & Cloud', employees: 9500, pipeline: 410 },
    { name: 'Corporate Functions', employees: 18500, pipeline: 300 },
  ],
};

// =============================================================================
// LEGAL FIRM - Am Law 100 Firm Demo
// =============================================================================
const LEGAL_FIRM_DATA: DemoClientData = {
  client: {
    id: 'legal',
    name: 'Morrison & Sterling LLP',
    industry: 'Legal Services',
    employeeCount: 4500,
    taUtilization: 32,
    planActivation: 48,
    completionRate: 89,
    internalMobility: 18,
  },
  criticalRoles: [
    { title: 'Associate Attorney', gap: 85, risk: 'critical', retirements: 12, pipeline: 45 },
    { title: 'Partner Track', gap: 22, risk: 'high', retirements: 8, pipeline: 15 },
    { title: 'Paralegal', gap: 45, risk: 'medium', retirements: 18, pipeline: 32 },
    { title: 'Legal Operations', gap: 12, risk: 'medium', retirements: 5, pipeline: 8 },
    { title: 'Practice Group Leader', gap: 8, risk: 'high', retirements: 6, pipeline: 4 },
  ],
  topPrograms: [
    {
      name: 'LL.M. in Tax Law',
      provider: 'NYU School of Law',
      type: 'Graduate',
      duration: '12 months',
      cost: '$68,000',
      completionRate: 95,
      roi: 285,
      enrollments: 24,
    },
    {
      name: 'Project Management for Legal',
      provider: 'ALSP Institute',
      type: 'Certificate',
      duration: '8 weeks',
      cost: '$2,500',
      completionRate: 92,
      roi: 320,
      enrollments: 156,
    },
    {
      name: 'Legal Technology Certificate',
      provider: 'Georgetown Law',
      type: 'Certificate',
      duration: '6 months',
      cost: '$8,500',
      completionRate: 88,
      roi: 195,
      enrollments: 68,
    },
    {
      name: 'Leadership for Lawyers',
      provider: 'Harvard Law School',
      type: 'Executive',
      duration: '2 weeks',
      cost: '$15,000',
      completionRate: 98,
      roi: 250,
      enrollments: 18,
    },
    {
      name: 'Paralegal Certificate',
      provider: 'ABA Approved Programs',
      type: 'Certificate',
      duration: '9 months',
      cost: '$12,000',
      completionRate: 85,
      roi: 180,
      enrollments: 42,
    },
  ],
  pipeline: {
    identified: 380,
    inProgram: 245,
    developing: 85,
    readyNow: 42,
    promoted: 28,
    attrition: 45,
  },
  competencies: [
    { name: 'Legal Research', avgScore: 92, target: 95, gap: 3 },
    { name: 'Client Relations', avgScore: 85, target: 92, gap: 7 },
    { name: 'Business Development', avgScore: 68, target: 85, gap: 17 },
    { name: 'Practice Management', avgScore: 72, target: 88, gap: 16 },
    { name: 'Technology Proficiency', avgScore: 65, target: 82, gap: 17 },
    { name: 'Leadership', avgScore: 78, target: 90, gap: 12 },
  ],
  departments: [
    { name: 'Corporate', employees: 1200, pipeline: 95 },
    { name: 'Litigation', employees: 1400, pipeline: 110 },
    { name: 'Tax & ERISA', employees: 450, pipeline: 35 },
    { name: 'Real Estate', employees: 380, pipeline: 28 },
    { name: 'Labor & Employment', employees: 520, pipeline: 42 },
    { name: 'Business Operations', employees: 550, pipeline: 25 },
  ],
};

// =============================================================================
// DAFFY (Generic Fortune 500)
// =============================================================================
const DAFFY_DATA: DemoClientData = {
  client: {
    id: 'daffy',
    name: 'Daffy Technologies',
    industry: 'Technology',
    employeeCount: 25000,
    taUtilization: 18,
    planActivation: 28,
    completionRate: 76,
    internalMobility: 15,
  },
  criticalRoles: [
    { title: 'Software Engineer', gap: 320, risk: 'critical', retirements: 45, pipeline: 180 },
    { title: 'Engineering Manager', gap: 42, risk: 'high', retirements: 18, pipeline: 28 },
    { title: 'Product Manager', gap: 65, risk: 'high', retirements: 12, pipeline: 35 },
    { title: 'Data Scientist', gap: 85, risk: 'medium', retirements: 8, pipeline: 45 },
    { title: 'UX Designer', gap: 38, risk: 'medium', retirements: 6, pipeline: 22 },
  ],
  topPrograms: [
    {
      name: 'AWS Solutions Architect',
      provider: 'Amazon Web Services',
      type: 'Certificate',
      duration: '12 weeks',
      cost: '$1,500',
      completionRate: 82,
      roi: 245,
      enrollments: 850,
    },
    {
      name: 'MBA - Technology Leadership',
      provider: 'Stanford GSB Online',
      type: 'Graduate',
      duration: '24 months',
      cost: '$75,000',
      completionRate: 78,
      roi: 185,
      enrollments: 45,
    },
    {
      name: 'Machine Learning Engineer',
      provider: 'Coursera / Google',
      type: 'Certificate',
      duration: '6 months',
      cost: '$1,200',
      completionRate: 72,
      roi: 210,
      enrollments: 420,
    },
    {
      name: 'Product Management',
      provider: 'Reforge',
      type: 'Certificate',
      duration: '8 weeks',
      cost: '$3,000',
      completionRate: 88,
      roi: 275,
      enrollments: 180,
    },
    {
      name: 'Cybersecurity Certificate',
      provider: 'SANS Institute',
      type: 'Certificate',
      duration: '16 weeks',
      cost: '$8,500',
      completionRate: 85,
      roi: 195,
      enrollments: 125,
    },
  ],
  pipeline: {
    identified: 2800,
    inProgram: 1650,
    developing: 580,
    readyNow: 245,
    promoted: 180,
    attrition: 320,
  },
  competencies: [
    { name: 'Technical Excellence', avgScore: 82, target: 92, gap: 10 },
    { name: 'Cloud Architecture', avgScore: 75, target: 88, gap: 13 },
    { name: 'Data & Analytics', avgScore: 72, target: 85, gap: 13 },
    { name: 'Leadership', avgScore: 78, target: 88, gap: 10 },
    { name: 'Product Thinking', avgScore: 70, target: 85, gap: 15 },
    { name: 'Security Awareness', avgScore: 68, target: 82, gap: 14 },
  ],
  departments: [
    { name: 'Engineering', employees: 12000, pipeline: 1200 },
    { name: 'Product', employees: 3500, pipeline: 280 },
    { name: 'Data Science', employees: 2200, pipeline: 180 },
    { name: 'Design', employees: 1800, pipeline: 120 },
    { name: 'Operations', employees: 3200, pipeline: 150 },
    { name: 'Corporate', employees: 2300, pipeline: 85 },
  ],
};

// =============================================================================
// EXPORTS
// =============================================================================

export const DEMO_CLIENTS: Record<DemoClientId, DemoClientData> = {
  lifepoint: LIFEPOINT_HEALTH,
  advocate: ADVOCATE_HEALTH,
  starbucks: STARBUCKS_DATA,
  delaware: STATE_OF_DELAWARE,
  'university-of-miami': UNIVERSITY_OF_MIAMI,
  'shelby-county': SHELBY_COUNTY_PUBLIC_SCHOOLS,
  verizon: VERIZON_DATA,
  ups: UPS_DATA,
  daffy: DAFFY_DATA,
  legal: LEGAL_FIRM_DATA,
};

export const DEMO_CLIENT_LIST: DemoClient[] = [
  LIFEPOINT_HEALTH.client,
  ADVOCATE_HEALTH.client,
  STARBUCKS_DATA.client,
  STATE_OF_DELAWARE.client,
  UNIVERSITY_OF_MIAMI.client,
  SHELBY_COUNTY_PUBLIC_SCHOOLS.client,
  VERIZON_DATA.client,
];

export function getDemoData(clientId: DemoClientId): DemoClientData {
  return DEMO_CLIENTS[clientId];
}

export function getDemoClientIdFromUrl(): DemoClientId {
  if (typeof window === 'undefined') {
    return 'advocate';
  }

  const params = new URLSearchParams(window.location.search);
  const client = params.get('demo_client') as DemoClientId | null;
  if (client && hasOwn(DEMO_CLIENTS, client)) {
    localStorage.setItem('reimagined-demo-client', client);
    return client;
  }

  // Fall back to localStorage if URL param is missing
  const stored = localStorage.getItem('reimagined-demo-client') as DemoClientId | null;
  if (stored && hasOwn(DEMO_CLIENTS, stored)) {
    return stored;
  }

  return 'advocate';
}

export function getCurrentDemoClientId(): DemoClientId {
  return getDemoClientIdFromUrl();
}

export function getCurrentDemoClientData(): DemoClientData {
  return getDemoData(getDemoClientIdFromUrl());
}

const DEMO_CLIENT_LOGO_COLORS: Record<DemoClientId, string> = {
  lifepoint: '#6d28d9',
  advocate: '#0f766e',
  starbucks: '#006241',
  delaware: '#1d4ed8',
  'university-of-miami': '#f97316',
  'shelby-county': '#7c3aed',
  verizon: '#dc2626',
  ups: '#7c2d12',
  daffy: '#2563eb',
  legal: '#374151',
};

function escapeSvgText(value: string): string {
  return value.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&apos;';
      default:
        return char;
    }
  });
}

function getClientInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

export function getDemoClientLogoUrl(clientId: DemoClientId): string {
  const client = getDemoData(clientId).client;
  if (client.logo) {
    return client.logo;
  }

  const color = DEMO_CLIENT_LOGO_COLORS[client.id];
  const initials = escapeSvgText(getClientInitials(client.name));
  const name = escapeSvgText(client.name);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="96" viewBox="0 0 320 96" role="img" aria-label="${name}"><rect width="320" height="96" rx="14" fill="white"/><circle cx="48" cy="48" r="30" fill="${color}"/><text x="48" y="58" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="24" font-weight="700" fill="white">${initials}</text><text x="94" y="57" font-family="Arial, Helvetica, sans-serif" font-size="27" font-weight="700" fill="${color}">${name}</text></svg>`;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

export function getCurrentDemoClientLogoUrl(): string {
  return getDemoClientLogoUrl(getCurrentDemoClientId());
}

export function setDemoClientId(clientId: DemoClientId): void {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.setItem('reimagined-demo-client', clientId);

  const url = new URL(window.location.href);
  url.searchParams.set('demo_client', clientId);
  window.location.assign(url.toString());
}

// =============================================================================
// SCENARIO HELPERS
// =============================================================================

export function getDemoScenario(scenarioId: DemoScenarioId): DemoScenario {
  return DEMO_SCENARIOS[scenarioId];
}

export function getDemoScenarioIdFromUrl(): DemoScenarioId {
  if (typeof window === 'undefined') {
    return 'full';
  }

  const params = new URLSearchParams(window.location.search);
  const product = params.get('demo_product') as DemoProductId | null;
  if (product && hasOwn(DEMO_PRODUCTS, product)) {
    const scenario = DEMO_PRODUCTS[product].scenarioId;
    localStorage.setItem('reimagined-demo-product', product);
    localStorage.setItem('reimagined-demo-scenario', scenario);
    return scenario;
  }

  const scenario = params.get('demo_scenario') as DemoScenarioId | null;
  if (scenario && hasOwn(DEMO_SCENARIOS, scenario)) {
    // Persist to localStorage so it survives page reloads
    localStorage.setItem('reimagined-demo-scenario', scenario);
    return scenario;
  }

  // Fall back to localStorage if URL param is missing (e.g. after reload)
  const stored = localStorage.getItem('reimagined-demo-scenario') as DemoScenarioId | null;
  if (stored && hasOwn(DEMO_SCENARIOS, stored)) {
    return stored;
  }

  return 'full';
}

export function getCurrentDemoScenarioId(): DemoScenarioId {
  return getDemoScenarioIdFromUrl();
}

export function getCurrentDemoScenario(): DemoScenario {
  return getDemoScenario(getDemoScenarioIdFromUrl());
}

// =============================================================================
// TAS SUB-SCENARIO HELPERS
// =============================================================================

export const DEMO_TAS_SCENARIO_LIST: DemoTasScenarioId[] = [
  'not-started',
  'in-progress',
  'approved',
  'rejected',
  'completed',
];

export function getDemoTasScenarioIdFromUrl(): DemoTasScenarioId | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const scenario = params.get('demo_ta_scenario') as DemoTasScenarioId | null;
  if (scenario && DEMO_TAS_SCENARIO_LIST.includes(scenario)) {
    localStorage.setItem('reimagined-demo-ta-scenario', scenario);
    return scenario;
  }

  // Fall back to localStorage if URL param is missing
  const stored = localStorage.getItem('reimagined-demo-ta-scenario') as DemoTasScenarioId | null;
  if (stored && DEMO_TAS_SCENARIO_LIST.includes(stored)) {
    return stored;
  }

  return null;
}

export function getCurrentDemoTasScenarioId(): DemoTasScenarioId {
  const urlScenario = getDemoTasScenarioIdFromUrl();
  if (urlScenario) {
    return urlScenario;
  }

  const scenario = getCurrentDemoScenario();
  if (!scenario.features.tuitionAssistance) {
    return 'not-started';
  }

  const userState = getDemoUserState();
  const taScenario = userState.taScenario;
  if (taScenario) {
    return taScenario;
  }

  if (userState.benefitsMaxedOut) {
    return 'completed';
  }

  if (userState.hasActiveTAApplication) {
    return 'in-progress';
  }

  return 'not-started';
}

export function setDemoTasScenarioId(scenarioId: DemoTasScenarioId): void {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.setItem('reimagined-demo-ta-scenario', scenarioId);

  const url = new URL(window.location.href);
  url.searchParams.set('demo_ta_scenario', scenarioId);
  window.location.assign(url.toString());
}

export function setDemoScenarioId(scenarioId: DemoScenarioId): void {
  if (typeof window === 'undefined') {
    return;
  }

  // Persist to localStorage for sticky scenario across reloads
  localStorage.setItem('reimagined-demo-scenario', scenarioId);

  const url = new URL(window.location.href);
  url.searchParams.set('demo_scenario', scenarioId);
  window.location.assign(url.toString());
}

/**
 * Check if a specific feature is enabled in the current scenario
 */
export function isFeatureEnabled(feature: keyof DemoScenario['features']): boolean {
  return getCurrentDemoScenario().features[feature];
}

/**
 * Get current user state. When a user-persona scenario is selected via the
 * `demo_user_scenario` toggle, its `userState` overrides the product-derived
 * scenario so the persona toggle actually changes onboarding/usage state
 * without disturbing product feature gates.
 */
export function getDemoUserState(): DemoScenario['userState'] {
  const personaId = getCurrentDemoUserScenarioId();
  const persona = DEMO_USER_SCENARIOS[personaId];
  const personaScenario = persona ? DEMO_SCENARIOS[persona.scenarioId] : undefined;
  return personaScenario?.userState ?? getCurrentDemoScenario().userState;
}

/**
 * Get current messaging. Mirrors `getDemoUserState`: the user-persona scenario
 * wins for messaging when set, otherwise the product scenario provides it.
 */
export function getDemoMessaging(): DemoScenario['messaging'] {
  const personaId = getCurrentDemoUserScenarioId();
  const persona = DEMO_USER_SCENARIOS[personaId];
  const personaScenario = persona ? DEMO_SCENARIOS[persona.scenarioId] : undefined;
  return personaScenario?.messaging ?? getCurrentDemoScenario().messaging;
}

// =============================================================================
// PSLF SUB-SCENARIO HELPERS
// =============================================================================

export const DEMO_PSLF_SCENARIO_LIST: DemoPslfScenarioId[] = [
  'blank-slate',
  'borrower-signed',
  'employer-signed',
  'submitted',
  'form-rejected',
  'multi-form',
  'with-nslds',
  'full-journey',
  'live-data',
];

export const DEMO_PSLF_SCENARIOS: Record<
  DemoPslfScenarioId,
  { name: string; description: string }
> = {
  'blank-slate': {
    name: 'New User',
    description: 'Eligible but not enrolled, no forms',
  },
  'borrower-signed': {
    name: 'Awaiting Employer',
    description: 'Borrower signed, waiting on employer signature',
  },
  'employer-signed': {
    name: 'Ready to Submit',
    description: 'Employer signed, ready to fax to DOE',
  },
  submitted: {
    name: 'Form Submitted',
    description: 'Fully signed and faxed to DOE',
  },
  'form-rejected': {
    name: 'Form Rejected',
    description: 'Employer rejected the most recent form',
  },
  'multi-form': {
    name: 'Multiple Forms',
    description: 'Current + prior employers in mixed states',
  },
  'with-nslds': {
    name: 'NSLDS Uploaded',
    description: 'Multiple forms + MyStudentData uploaded',
  },
  'full-journey': {
    name: 'Complete Journey',
    description: 'Forms + NSLDS + forgiveness estimator completed',
  },
  'live-data': {
    name: 'Live Employee Data',
    description: 'Use real data from the logged-in employee',
  },
};

export function getDemoPslfScenarioIdFromUrl(): DemoPslfScenarioId | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const scenario = params.get('demo_pslf_scenario') as DemoPslfScenarioId | null;
  if (scenario && DEMO_PSLF_SCENARIO_LIST.includes(scenario)) {
    localStorage.setItem('reimagined-demo-pslf-scenario', scenario);
    return scenario;
  }

  // Fall back to localStorage if URL param is missing
  const stored = localStorage.getItem('reimagined-demo-pslf-scenario') as DemoPslfScenarioId | null;
  if (stored && DEMO_PSLF_SCENARIO_LIST.includes(stored)) {
    return stored;
  }

  return null;
}

export function getCurrentDemoPslfScenarioId(): DemoPslfScenarioId {
  return getDemoPslfScenarioIdFromUrl() ?? 'blank-slate';
}

export function setDemoPslfScenarioId(scenarioId: DemoPslfScenarioId): void {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.setItem('reimagined-demo-pslf-scenario', scenarioId);

  const url = new URL(window.location.href);
  url.searchParams.set('demo_pslf_scenario', scenarioId);
  window.location.assign(url.toString());
}

// =============================================================================
// DEMO SESSION HELPERS
// =============================================================================

export function getDemoProduct(productId: DemoProductId): DemoProduct {
  return DEMO_PRODUCTS[productId];
}

export function getDemoSessionRole(roleId: DemoSessionRoleId): DemoSessionRole {
  return DEMO_SESSION_ROLES[roleId];
}

export function getDemoUserScenario(scenarioId: DemoUserScenarioId): DemoUserScenario {
  return DEMO_USER_SCENARIOS[scenarioId];
}

export function getDemoTaPaymentModel(modelId: DemoTaPaymentModelId): DemoTaPaymentModel {
  return DEMO_TA_PAYMENT_MODELS[modelId];
}

/**
 * Whether the product's scenario enables Tuition Assistance. The TA payment
 * model configures TA-only behavior — the assistance manner and the
 * direct-pay/card-review keys on the `tuitionAssistance` settings block — so
 * the picker axis (and its persistence) only applies to products that pass
 * this check.
 */
export function productHasTuitionAssistance(productId: DemoProductId): boolean {
  return getDemoScenario(getDemoProduct(productId).scenarioId).features.tuitionAssistance;
}

function getTasScenarioIdForDemoSession(
  product: DemoProduct,
  userScenarioId: DemoUserScenarioId
): DemoTasScenarioId | undefined {
  if (!product.tasScenarioId) {
    return undefined;
  }

  const userScenario = getDemoUserScenario(userScenarioId);
  const userStateScenario = getDemoScenario(userScenario.scenarioId);
  return userStateScenario.userState.taScenario ?? product.tasScenarioId;
}

export function getDemoProductIdFromUrl(): DemoProductId {
  if (typeof window === 'undefined') {
    return 'flexible-choice-plan';
  }

  const params = new URLSearchParams(window.location.search);
  const product = params.get('demo_product') as DemoProductId | null;
  if (product && hasOwn(DEMO_PRODUCTS, product)) {
    localStorage.setItem('reimagined-demo-product', product);
    return product;
  }

  const stored = localStorage.getItem('reimagined-demo-product') as DemoProductId | null;
  if (stored && hasOwn(DEMO_PRODUCTS, stored)) {
    return stored;
  }

  const scenario =
    (params.get('demo_scenario') as DemoScenarioId | null) ??
    (localStorage.getItem('reimagined-demo-scenario') as DemoScenarioId | null);
  const productForScenario = DEMO_PRODUCT_LIST.find((item) => item.scenarioId === scenario);
  return productForScenario?.id ?? 'flexible-choice-plan';
}

export function getCurrentDemoProductId(): DemoProductId {
  return getDemoProductIdFromUrl();
}

export function getCurrentDemoProduct(): DemoProduct {
  return getDemoProduct(getCurrentDemoProductId());
}

export function getDemoRoleIdFromUrl(): DemoSessionRoleId {
  if (typeof window === 'undefined') {
    return 'employee';
  }

  const params = new URLSearchParams(window.location.search);
  const isDemoMode = params.get('demo') === 'true' || hasStickyDemoMode(window.location.pathname);
  const role = params.get('demo_role') as DemoSessionRoleId | null;
  if (isDemoMode && role && hasOwn(DEMO_SESSION_ROLES, role)) {
    localStorage.setItem('reimagined-demo-role', role);
    return role;
  }

  const stored = localStorage.getItem('reimagined-demo-role') as DemoSessionRoleId | null;
  if (isDemoMode && stored && hasOwn(DEMO_SESSION_ROLES, stored)) {
    return stored;
  }

  const currentRelationshipType = localStorage.getItem('role');
  const roleForRelationship = DEMO_SESSION_ROLE_LIST.find(
    (item) => item.relationshipType === currentRelationshipType
  );
  return roleForRelationship?.id ?? 'employee';
}

export function getCurrentDemoRoleId(): DemoSessionRoleId {
  return getDemoRoleIdFromUrl();
}

export function getCurrentDemoRole(): DemoSessionRole {
  return getDemoSessionRole(getCurrentDemoRoleId());
}

function getDefaultUserScenarioIdForProduct(productId: DemoProductId): DemoUserScenarioId {
  if (productId === 'flexible-choice-plan') {
    return 'flex-choice-hsa';
  }

  const product = DEMO_PRODUCTS[productId];
  const matching = DEMO_USER_SCENARIO_LIST.find((item) => item.scenarioId === product.scenarioId);
  return matching?.id ?? 'power-user';
}

export function getDemoUserScenarioIdFromUrl(): DemoUserScenarioId {
  if (typeof window === 'undefined') {
    return 'power-user';
  }

  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('demo_user_scenario') as DemoUserScenarioId | null;
  if (fromUrl && hasOwn(DEMO_USER_SCENARIOS, fromUrl)) {
    localStorage.setItem('reimagined-demo-user-scenario', fromUrl);
    return fromUrl;
  }

  const stored = localStorage.getItem('reimagined-demo-user-scenario') as DemoUserScenarioId | null;
  if (stored && hasOwn(DEMO_USER_SCENARIOS, stored)) {
    return stored;
  }

  return getDefaultUserScenarioIdForProduct(getDemoProductIdFromUrl());
}

export function getCurrentDemoUserScenarioId(): DemoUserScenarioId {
  return getDemoUserScenarioIdFromUrl();
}

export function getCurrentDemoUserScenario(): DemoUserScenario {
  return getDemoUserScenario(getCurrentDemoUserScenarioId());
}

export function getDemoTaPaymentModelIdFromUrl(): DemoTaPaymentModelId {
  if (typeof window === 'undefined') {
    return 'reimbursement';
  }

  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('demo_ta_payment_model') as DemoTaPaymentModelId | null;
  if (fromUrl && hasOwn(DEMO_TA_PAYMENT_MODELS, fromUrl)) {
    localStorage.setItem('reimagined-demo-ta-payment-model', fromUrl);
    return fromUrl;
  }

  const stored = localStorage.getItem(
    'reimagined-demo-ta-payment-model'
  ) as DemoTaPaymentModelId | null;
  if (stored && hasOwn(DEMO_TA_PAYMENT_MODELS, stored)) {
    return stored;
  }

  return 'reimbursement';
}

export function getCurrentDemoTaPaymentModelId(): DemoTaPaymentModelId {
  return getDemoTaPaymentModelIdFromUrl();
}

export function getCurrentDemoTaPaymentModel(): DemoTaPaymentModel {
  return getDemoTaPaymentModel(getCurrentDemoTaPaymentModelId());
}

function buildDemoCompanySettings(): DemoCompanySettings {
  const client = getCurrentDemoClientData().client;
  const scenario = getCurrentDemoScenario();
  const product = getCurrentDemoProduct();
  const paymentModel = getCurrentDemoTaPaymentModelId();
  const subDomain = client.id;
  const supportDisabled = {
    isEnabled: false,
  };

  return {
    id: `demo-company-setting-${client.id}`,
    subDomain,
    partnerType: scenario.features.pslf ? 'PSLF_ELIGIBLE' : 'PRIVATE',
    allowFamilyPlan: product.id === 'flexible-choice-plan',
    newDesign: true,
    returnToPartnerUrl: {
      base: 'example.com',
      subdomain: subDomain,
      path: 'benefits',
    },
    register: {
      'is-email-register-allowed': true,
      'show-countdown': false,
      'is-payroll-id-and-last-name-allowed': false,
      'require-custom-terms': false,
      'custom-terms-text': '',
      'is-self-register-allowed': true,
      'self-register-domain': '',
    },
    security: {
      passwordMinLength: 8,
    },
    modules: {
      pslf: {
        groups: [],
        enabled: scenario.features.pslf,
        employer: {
          services: [],
          isPolitical: false,
          isTaxExempt: scenario.features.pslf,
          isGovernment: client.industry === 'Government' || client.industry === 'Education',
          isNotForProfit: scenario.features.pslf,
          legalName: client.name,
          fein: '00-0000000',
          website: `https://${subDomain}.example.com`,
          address: {
            addressLine1: '123 Demo Way',
            addressLine2: '',
            city: 'Demo City',
            state: 'CA',
            postalCode: '90000',
          },
        },
        enabledOn: '2025-01-01',
        isNonPartnerEmployer: false,
        isEnabledPriorEmployer: scenario.features.pslf,
        isNonPartnerApprover: false,
      },
      syf: {
        isEnabled: scenario.features.employerMatch,
      },
      support: {
        faqs: supportDisabled,
        zendesk: supportDisabled,
        tioEmail: supportDisabled,
        tioPhone: supportDisabled,
      },
    },
    tuitionAssistance: {
      taOnlyPlanId: `demo-ta-${client.id}`,
      isTaMentoringEnabled: scenario.features.careerPathways,
      isTaOnly: product.id === 'tuition-assistance',
      isTaEnabled: scenario.features.tuitionAssistance,
      eligibilityWaitingPeriodDays: 0,
      isDependentProgramsEnabled: product.id === 'flexible-choice-plan',
      isDirectPayEnabled: paymentModel === 'prepayment',
      isPartnerMessagingEnabled: false,
      isPartnerMessagingEmailEnabled: false,
      enableAgentRecommendations: scenario.features.careerPathways,
      hideAnnualBenefitsCard: false,
      isCardUsageReviewEnabled: paymentModel === 'prepayment',
    },
    lpn: {
      isEnabled: scenario.features.tuitionAssistance,
    },
    quickSight: {
      dashboards: [],
    },
  };
}

function buildDemoPlans(): DemoPlanSummary[] {
  const scenario = getCurrentDemoScenario();
  const client = getCurrentDemoClientData().client;
  const plans: DemoPlanSummary[] = [];

  if (scenario.features.financialWellness) {
    plans.push({
      id: `demo-wellness-plan-${client.id}`,
      name: `${client.name} Financial Wellness`,
      cadence: 'NEVER',
      isWellness: true,
      annualLimit: 0,
      lifetimeLimit: 0,
      tiers: [],
    });
  }

  if (scenario.features.studentLoanRepayment || scenario.features.employerMatch) {
    plans.push({
      id: `demo-contribution-plan-${client.id}`,
      name: `${client.name} Student Loan Benefit`,
      cadence: 'MONTHLY',
      isWellness: false,
      annualLimit: 5250,
      lifetimeLimit: 20000,
      tiers: [{ amount: scenario.features.employerMatch ? 100 : 125 }],
    });
  }

  return plans;
}

export function getCurrentDemoCompanySettings(): DemoCompanySettings {
  return buildDemoCompanySettings();
}

export function getCurrentDemoPlans(): DemoPlanSummary[] {
  return buildDemoPlans();
}

export function getCurrentDemoPreRegisterCompany(): DemoPreRegisterCompany {
  const client = getCurrentDemoClientData().client;
  const settings = getCurrentDemoCompanySettings();
  const product = getCurrentDemoProduct();

  return {
    id: `demo-company-${client.id}`,
    name: client.name,
    launchDate: '2020-01-01',
    programTitle:
      product.id === 'tuition-assistance' && client.id === 'starbucks'
        ? 'Starbucks College Achievement Plan'
        : 'Education Assistance Program',
    taEnabled: getCurrentDemoScenario().features.tuitionAssistance,
    settings,
  };
}

export function getCurrentDemoCompany(): DemoPartnerCompany {
  const preRegisterCompany = getCurrentDemoPreRegisterCompany();
  const settings = getCurrentDemoCompanySettings();

  return {
    ...preRegisterCompany,
    settings,
    companySetting: settings,
    plans: getCurrentDemoPlans(),
  };
}

/**
 * Resolve the EFFECTIVE session axes for a selection — the single place the
 * apply path decides what persists, shared by {@link buildDemoSessionUrl} (URL
 * params) and {@link applyDemoSession} (localStorage) so the two stores can
 * never disagree:
 *
 * - User scenario: the personas are PARTICIPANT-only employee journeys. The
 *   picker hides the section for supervisor/admin, but a previously chosen or
 *   stored persona would still ride along and leak into the fabricated `/me`
 *   identity name — so non-participant sessions persist NO persona, and the TA
 *   sub-scenario derives from the product alone (no persona override).
 * - TA payment model: only meaningful when the product's scenario enables
 *   tuition assistance (it feeds the direct-pay/card-review keys on the
 *   `tuitionAssistance` settings block); for non-TA products a stale or
 *   hand-edited non-default model is coerced back to the default.
 */
function resolveDemoSessionAxes(selection: DemoSessionSelection): {
  product: DemoProduct;
  role: DemoSessionRole;
  userScenarioId?: DemoUserScenarioId;
  taPaymentModelId: DemoTaPaymentModelId;
  tasScenarioId?: DemoTasScenarioId;
} {
  const product = getDemoProduct(selection.productId);
  const role = getDemoSessionRole(selection.roleId);
  const userScenarioId =
    role.relationshipType === 'PARTICIPANT'
      ? (selection.userScenarioId ?? getDefaultUserScenarioIdForProduct(selection.productId))
      : undefined;
  const taPaymentModelId = productHasTuitionAssistance(selection.productId)
    ? (selection.taPaymentModelId ?? 'reimbursement')
    : 'reimbursement';
  const tasScenarioId = userScenarioId
    ? getTasScenarioIdForDemoSession(product, userScenarioId)
    : product.tasScenarioId;

  return { product, role, userScenarioId, taPaymentModelId, tasScenarioId };
}

export function buildDemoSessionUrl(currentHref: string, selection: DemoSessionSelection): string {
  const { product, userScenarioId, taPaymentModelId, tasScenarioId } =
    resolveDemoSessionAxes(selection);
  const url = new URL(currentHref);

  url.searchParams.set('demo', 'true');
  url.searchParams.set('demo_client', selection.clientId);
  url.searchParams.set('demo_product', selection.productId);
  url.searchParams.set('demo_role', selection.roleId);
  if (userScenarioId) {
    url.searchParams.set('demo_user_scenario', userScenarioId);
  } else {
    url.searchParams.delete('demo_user_scenario');
  }
  url.searchParams.set('demo_ta_payment_model', taPaymentModelId);
  url.searchParams.set('demo_scenario', product.scenarioId);

  if (tasScenarioId) {
    url.searchParams.set('demo_ta_scenario', tasScenarioId);
  } else {
    url.searchParams.delete('demo_ta_scenario');
  }

  if (product.pslfScenarioId) {
    url.searchParams.set('demo_pslf_scenario', product.pslfScenarioId);
  } else {
    url.searchParams.delete('demo_pslf_scenario');
  }

  return url.toString();
}

export function applyDemoSession(selection: DemoSessionSelection): void {
  if (typeof window === 'undefined') {
    return;
  }

  const { product, role, userScenarioId, taPaymentModelId, tasScenarioId } =
    resolveDemoSessionAxes(selection);

  localStorage.setItem('reimagined-demo-mode', 'true');
  localStorage.setItem('reimagined-demo-client', selection.clientId);
  localStorage.setItem('reimagined-demo-product', selection.productId);
  localStorage.setItem('reimagined-demo-role', selection.roleId);
  if (userScenarioId) {
    localStorage.setItem('reimagined-demo-user-scenario', userScenarioId);
  } else {
    // Clear any stale persona so the sticky read path cannot resurrect it for
    // a non-participant session.
    localStorage.removeItem('reimagined-demo-user-scenario');
  }
  localStorage.setItem('reimagined-demo-ta-payment-model', taPaymentModelId);
  localStorage.setItem('reimagined-demo-scenario', product.scenarioId);
  // Overload the shared `role` key with the demo relationship type, and mark that
  // we did so — clearDemoSession() restores (clears) `role` only when this marker
  // is present, so a real user's role selection is never wiped by the in-page
  // toggle or a pure `?demo=true` view (which never overload `role`).
  localStorage.setItem('role', role.relationshipType);
  localStorage.setItem('reimagined-demo-role-overloaded', 'true');

  if (tasScenarioId) {
    localStorage.setItem('reimagined-demo-ta-scenario', tasScenarioId);
  } else {
    localStorage.removeItem('reimagined-demo-ta-scenario');
  }

  if (product.pslfScenarioId) {
    localStorage.setItem('reimagined-demo-pslf-scenario', product.pslfScenarioId);
  } else {
    localStorage.removeItem('reimagined-demo-pslf-scenario');
  }

  window.location.assign(buildDemoSessionUrl(window.location.href, selection));
}

export function setDemoProductId(productId: DemoProductId): void {
  applyDemoSession({
    clientId: getCurrentDemoClientId(),
    productId,
    roleId: getCurrentDemoRoleId(),
    userScenarioId: getCurrentDemoUserScenarioId(),
    taPaymentModelId: getCurrentDemoTaPaymentModelId(),
  });
}

export function setDemoRoleId(roleId: DemoSessionRoleId): void {
  applyDemoSession({
    clientId: getCurrentDemoClientId(),
    productId: getCurrentDemoProductId(),
    roleId,
    userScenarioId: getCurrentDemoUserScenarioId(),
    taPaymentModelId: getCurrentDemoTaPaymentModelId(),
  });
}

export function setDemoUserScenarioId(userScenarioId: DemoUserScenarioId): void {
  applyDemoSession({
    clientId: getCurrentDemoClientId(),
    productId: getCurrentDemoProductId(),
    roleId: getCurrentDemoRoleId(),
    userScenarioId,
    taPaymentModelId: getCurrentDemoTaPaymentModelId(),
  });
}

export function setDemoTaPaymentModelId(taPaymentModelId: DemoTaPaymentModelId): void {
  applyDemoSession({
    clientId: getCurrentDemoClientId(),
    productId: getCurrentDemoProductId(),
    roleId: getCurrentDemoRoleId(),
    userScenarioId: getCurrentDemoUserScenarioId(),
    taPaymentModelId,
  });
}
